import { useEffect, useRef, useState } from "react";
import { CITIES } from "./maptapCities";
import "./maptap.css";

const STORAGE_KEY = "maptap_endless_v3";
const EARTH_RADIUS_MILES = 3958.7613;
const CITY_BY_ID = new Map(CITIES.map((city) => [city.id, city]));
const COUNTRY_NAMES =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const SATELLITE_STYLE = {
  version: 8,
  projection: { type: "globe" },
  sources: {
    satellite: {
      type: "raster",
      tiles: [
        "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution:
        "Source: Esri, Vantor, Earthstar Geographics, and the GIS User Community",
    },
  },
  layers: [
    {
      id: "satellite",
      type: "raster",
      source: "satellite",
    },
  ],
};

function getWorldView() {
  return {
    center: [0, 18],
    zoom: window.innerWidth < 640 ? 0.9 : 1.55,
  };
}

function shuffle(values) {
  const shuffled = [...values];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

function createFreshGame(round = 1, previousCityId = null) {
  const deck = shuffle(CITIES.map((city) => city.id));

  if (deck[0] === previousCityId && deck.length > 1) {
    [deck[0], deck[1]] = [deck[1], deck[0]];
  }

  return {
    cityId: deck[0],
    remaining: deck.slice(1),
    round,
  };
}

function createInitialGame() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    const remaining = Array.isArray(saved?.remaining) ? saved.remaining : [];
    const ids = [saved?.cityId, ...remaining];

    if (
      CITY_BY_ID.has(saved?.cityId) &&
      ids.every((id) => CITY_BY_ID.has(id)) &&
      new Set(ids).size === ids.length &&
      Number.isInteger(saved?.round) &&
      saved.round > 0
    ) {
      return saved;
    }
  } catch {
    // A blocked or stale localStorage value should not stop a new game.
  }

  return createFreshGame();
}

function toRadians(value) {
  return (value * Math.PI) / 180;
}

function getDistanceMiles(first, second) {
  const deltaLat = toRadians(second.lat - first.lat);
  const deltaLng = toRadians(second.lng - first.lng);
  const lat1 = toRadians(first.lat);
  const lat2 = toRadians(second.lat);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;

  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(miles) {
  if (miles < 10) {
    return `${miles.toFixed(1)} miles`;
  }

  return `${Math.round(miles).toLocaleString()} miles`;
}

function getCountryName(countryCode) {
  try {
    return COUNTRY_NAMES?.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
}

function getLocationDescription(city) {
  return [
    ...new Set(
      [city.admin1Name, getCountryName(city.countryCode)].filter(Boolean)
    ),
  ].join(", ");
}

function createMarker(type, label) {
  const marker = document.createElement("div");
  marker.className = `maptapMarker maptapMarker-${type}`;
  marker.setAttribute("aria-hidden", "true");

  const dot = document.createElement("span");
  dot.className = "maptapMarkerDot";
  dot.textContent = label;
  marker.append(dot);

  return marker;
}

function clearMarkers(markers) {
  markers.current.forEach((marker) => marker.remove());
  markers.current = [];
}

function getRevealBounds(guess, answer) {
  let answerLng = answer.lng;

  if (Math.abs(answerLng - guess.lng) > 180) {
    answerLng += answerLng < guess.lng ? 360 : -360;
  }

  return [
    [Math.min(guess.lng, answerLng), Math.min(guess.lat, answer.lat)],
    [Math.max(guess.lng, answerLng), Math.max(guess.lat, answer.lat)],
  ];
}

function MaptapPage() {
  const [game, setGame] = useState(createInitialGame);
  const [result, setResult] = useState(null);
  const [mapStatus, setMapStatus] = useState("loading");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const resultRef = useRef(result);
  const guessHandlerRef = useRef(null);
  const city = CITY_BY_ID.get(game.cityId);
  const answerKind = city.type === "island" ? "island" : "location";

  resultRef.current = result;
  guessHandlerRef.current = (guess) => {
    if (resultRef.current) {
      return;
    }

    const nextResult = {
      guess,
      distanceMiles: getDistanceMiles(guess, city.coordinates),
    };
    resultRef.current = nextResult;
    setResult(nextResult);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(game));
    } catch {
      // The current session can continue when localStorage is unavailable.
    }
  }, [game]);

  useEffect(() => {
    const maplibregl = window.maplibregl;

    if (!mapContainerRef.current || !maplibregl) {
      setMapStatus("error");
      return undefined;
    }

    const map = new maplibregl.Map({
      attributionControl: false,
      center: getWorldView().center,
      container: mapContainerRef.current,
      dragRotate: false,
      maxZoom: 6,
      minZoom: 0,
      pitchWithRotate: false,
      style: SATELLITE_STYLE,
      zoom: getWorldView().zoom,
    });

    mapRef.current = map;
    // Results and wrapped labels can change the map height without a window resize.
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => map.resize())
      : null;
    resizeObserver?.observe(mapContainerRef.current);
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "bottom-right"
    );
    map.getCanvas().style.cursor = "crosshair";

    map.on("click", (event) => {
      guessHandlerRef.current?.({
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      });
    });

    map.on("load", () => {
      map.addSource("guess-line", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "guess-line",
        type: "line",
        source: "guess-line",
        paint: {
          "line-color": "#ffffff",
          "line-dasharray": [1.5, 1.5],
          "line-opacity": 0.9,
          "line-width": 2,
        },
      });
      setMapStatus("ready");
      map.resize();
    });

    return () => {
      resizeObserver?.disconnect();
      clearMarkers(markersRef);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const maplibregl = window.maplibregl;

    if (mapStatus !== "ready" || !map || !maplibregl) {
      return;
    }

    clearMarkers(markersRef);
    const lineSource = map.getSource("guess-line");

    if (!result) {
      lineSource?.setData({ type: "FeatureCollection", features: [] });
      map.easeTo({ ...getWorldView(), duration: 550 });
      return;
    }

    const answer = city.coordinates;
    markersRef.current = [
      new maplibregl.Marker({
        anchor: "center",
        element: createMarker("guess", "G"),
      })
        .setLngLat([result.guess.lng, result.guess.lat])
        .addTo(map),
      new maplibregl.Marker({
        anchor: "center",
        element: createMarker("answer", "L"),
      })
        .setLngLat([answer.lng, answer.lat])
        .addTo(map),
    ];

    lineSource?.setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [
          [result.guess.lng, result.guess.lat],
          [answer.lng, answer.lat],
        ],
      },
    });

    if (result.distanceMiles < 1) {
      map.easeTo({ center: [answer.lng, answer.lat], zoom: 6, duration: 800 });
    } else {
      map.fitBounds(getRevealBounds(result.guess, answer), {
        duration: 800,
        maxZoom: 5,
        padding: { top: 180, right: 80, bottom: 220, left: 80 },
      });
    }
  }, [city, mapStatus, result]);

  function startNextRound() {
    setResult(null);
    resultRef.current = null;
    setGame((current) => {
      if (!current.remaining.length) {
        return createFreshGame(current.round + 1, current.cityId);
      }

      return {
        cityId: current.remaining[0],
        remaining: current.remaining.slice(1),
        round: current.round + 1,
      };
    });
  }

  const distanceKm = result ? result.distanceMiles * 1.609344 : null;

  return (
    <main className="maptapPage">
      <div
        className="maptapMap"
        ref={mapContainerRef}
        role="application"
        aria-label={`Satellite globe. Tap where you think ${city.name} is.`}
      />

      <header className="maptapTopbar">
        <a href="/" className="maptapBrand" aria-label="Back to home">
          MapTap <span>Endless</span>
        </a>
        <div className="maptapProgress">
          Round {game.round.toLocaleString()}
          <span aria-hidden="true">·</span>
          {game.remaining.length.toLocaleString()} unseen
        </div>
      </header>

      <section className="maptapPrompt" aria-live="polite">
        <p>{result ? `The ${answerKind} was` : "Tap where you think this is"}</p>
        <h1>{city.name}</h1>
        <span>{getLocationDescription(city)}</span>
      </section>

      {mapStatus === "loading" && (
        <div className="maptapMapNotice">Loading satellite globe…</div>
      )}
      {mapStatus === "error" && (
        <div className="maptapMapNotice isError">
          The satellite globe could not load.
        </div>
      )}

      {!result && mapStatus === "ready" && (
        <p className="maptapHint">Drag to move · scroll or pinch to zoom · tap to guess</p>
      )}

      {result && (
        <section className="maptapResult" aria-live="assertive">
          <div className="maptapDistance">
            <p>Your guess was</p>
            <strong>{formatDistance(result.distanceMiles)}</strong>
            <span>{Math.round(distanceKm).toLocaleString()} km away</span>
          </div>
          <div className="maptapLegend" aria-label="Map marker legend">
            <span><i className="isGuess" /> Your guess</span>
            <span><i className="isAnswer" /> Location</span>
          </div>
          <button type="button" onClick={startNextRound}>
            Next location <span aria-hidden="true">→</span>
          </button>
        </section>
      )}

      <footer className="maptapAttribution">
        Imagery © Esri and contributors · City data © {" "}
        <a href="https://www.geonames.org/" target="_blank" rel="noreferrer">
          GeoNames
        </a>
      </footer>
    </main>
  );
}

export default MaptapPage;
